import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { Shop } from '../types';
import { getApiUrl, fetchWithCredentials } from '../lib/config';

// ==========================================
// Types
// ==========================================

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  shop: Shop | null;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  login: (shopSlug: string, pin: string) => Promise<boolean>;
  logout: () => Promise<void>;
}

// ==========================================
// Context
// ==========================================

const AuthContext = createContext<AuthContextValue | null>(null);

// ==========================================
// Provider
// ==========================================

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    shop: null,
    error: null,
  });

  // Check existing session on mount
  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async () => {
    try {
      const response = await fetch(getApiUrl('/api/cashier/me'), {
        ...fetchWithCredentials,
      });

      if (response.ok) {
        const data = await response.json();
        setState({
          isAuthenticated: true,
          isLoading: false,
          shop: data.shop,
          error: null,
        });
      } else {
        setState({
          isAuthenticated: false,
          isLoading: false,
          shop: null,
          error: null,
        });
      }
    } catch {
      setState({
        isAuthenticated: false,
        isLoading: false,
        shop: null,
        error: null,
      });
    }
  };

  const login = useCallback(async (shopSlug: string, pin: string): Promise<boolean> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await fetch(getApiUrl('/api/cashier/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        ...fetchWithCredentials,
        body: JSON.stringify({ shopSlug, pin }),
      });

      if (response.ok) {
        const data = await response.json();
        setState({
          isAuthenticated: true,
          isLoading: false,
          shop: data.shop,
          error: null,
        });
        return true;
      } else {
        const error = await response.json();
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: error.error || 'فشل تسجيل الدخول',
        }));
        return false;
      }
    } catch {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: 'حدث خطأ في الاتصال',
      }));
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(getApiUrl('/api/cashier/logout'), {
        method: 'POST',
        ...fetchWithCredentials,
      });
    } finally {
      setState({
        isAuthenticated: false,
        isLoading: false,
        shop: null,
        error: null,
      });
    }
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// ==========================================
// Hook
// ==========================================

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
